/**
 * Feedback Export Utilities
 * Functions for exporting and sharing feedback data
 */

import jsPDF from "jspdf";
import {
  FeedbackBlock,
  GaugeBlock,
  ScoreGridBlock,
  TextBlock,
  TranscriptBlock,
} from "@/types/interview-feedback.types";
import { processScore } from "./scoreUtils";
import { logger } from "@/utils/logger"
/**
 * Generate PDF from feedback blocks using native jsPDF text rendering
 * This produces much smaller files with proper page breaks
 */
export const generateFeedbackPDF = async (
  blocks: FeedbackBlock[],
  overallScore: {
    value: number;
    maxValue: number;
    label: string;
    color: string;
  } | null,
  completedAt: string,
  fileName: string = "conversation-feedback.pdf"
): Promise<void> => {
  logger.log("🔍 [PDF Export] Starting PDF generation...", {
    blocksCount: blocks.length,
  });

  try {
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: true,
    });

    // Usar Helvetica (fonte padrão)
    pdf.setFont("helvetica", "normal");

    // Constants
    const margin = 15; // 1.5cm
    const pageWidth = 210;
    const pageHeight = 297;
    const contentWidth = pageWidth - margin * 2;
    const maxY = pageHeight - margin;

    let currentY = margin;
    const lineHeight = 6;
    const sectionGap = 8;

    // Helper function to check if we need a new page
    const checkPageBreak = (requiredSpace: number = lineHeight) => {
      if (currentY + requiredSpace > maxY) {
        pdf.addPage();
        currentY = margin;
        return true;
      }
      return false;
    };

    // Helper to add text with word wrap
    const addWrappedText = (
      text: string,
      x: number,
      fontSize: number,
      maxWidth: number,
      isBold: boolean = false
    ) => {
      pdf.setFontSize(fontSize);
      pdf.setFont("helvetica", isBold ? "bold" : "normal");
      const lines = pdf.splitTextToSize(text, maxWidth);

      for (const line of lines) {
        checkPageBreak();
        logger.log(
          "[PDF] Linha sendo renderizada:",
          line,
          "chars:",
          [...line].map((c) => c.charCodeAt(0))
        );
        pdf.text(line, x, currentY);
        currentY += lineHeight;
      }
    };

    // === HEADER ===
    pdf.setFontSize(20);
    pdf.setFont("helvetica", "bold");
    pdf.text("Conversation Performance Feedback", margin, currentY);
    currentY += 10;

    pdf.setFontSize(10);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(100, 100, 100);
    const date = new Date(completedAt).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    pdf.text(`Completed on ${date}`, margin, currentY);
    currentY += sectionGap;
    pdf.setTextColor(0, 0, 0);

    // Line separator
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, currentY, pageWidth - margin, currentY);
    currentY += sectionGap;

    // === OVERALL SCORE ===
    if (overallScore) {
      checkPageBreak(20);

      // Use processScore para garantir mesmas cores da página
      const scoreData = processScore(overallScore.value, overallScore.maxValue);

      // Helper to convert hex to RGB
      const hexToRgb = (hex: string) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result
          ? {
              r: parseInt(result[1], 16),
              g: parseInt(result[2], 16),
              b: parseInt(result[3], 16),
            }
          : { r: 0, g: 0, b: 0 };
      };

      const rgb = hexToRgb(scoreData.color);

      // Background cinza claro
      pdf.setFillColor(245, 245, 245);
      pdf.rect(margin, currentY, contentWidth, 20, "F");

      pdf.setFontSize(14);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(0, 0, 0);
      pdf.text("Overall Performance", margin + 5, currentY + 8);

      pdf.setFontSize(24);
      pdf.setTextColor(rgb.r, rgb.g, rgb.b);
      pdf.text(
        `${scoreData.value.toFixed(1)}/10`,
        pageWidth - margin - 5,
        currentY + 14,
        { align: "right" }
      );
      pdf.setTextColor(0, 0, 0);

      currentY += 25;
    }

    // === BLOCKS ===
    const sortedBlocks = [...blocks].sort((a, b) => a.position - b.position);

    for (const block of sortedBlocks) {
      checkPageBreak(15);

      // Block title (não mostrar para gauge)
      if (block.type !== "gauge") {
        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        pdf.text(block.title, margin, currentY);
        currentY += 8;

        // Helper text (não mostrar para gauge)
        if (block.helper) {
          pdf.setFontSize(9);
          pdf.setFont("helvetica", "italic");
          pdf.setTextColor(100, 100, 100);
          addWrappedText(block.helper, margin, 9, contentWidth, false);
          pdf.setTextColor(0, 0, 0);
          currentY += 3;
        }
      }

      // Render based on type
      if (block.type === "score_grid") {
        const scoreGrid = block as ScoreGridBlock;
        for (const item of scoreGrid.items) {
          checkPageBreak(12);

          pdf.setFontSize(11);
          pdf.setFont("helvetica", "bold");
          pdf.text(item.title, margin + 5, currentY);

          // MESMA LÓGICA DO ScoreGridBlock.tsx
          // Se score > maxScore, divide por 10 (normalização)
          let displayScore = item.score;
          if (item.score > item.maxScore) {
            displayScore = item.score / 10;
          }

          // Clamp to valid range
          displayScore = Math.min(Math.max(displayScore, 0), item.maxScore);

          pdf.setFont("helvetica", "normal");
          const scoreText = `${displayScore.toFixed(1)}/${item.maxScore}`;
          pdf.text(scoreText, pageWidth - margin - 5, currentY, {
            align: "right",
          });

          currentY += 6;

          // Progress bar
          const barWidth = contentWidth - 10;
          const barHeight = 4;
          const percentage = (displayScore / item.maxScore) * 100;
          const fillWidth = (percentage / 100) * barWidth;

          pdf.setFillColor(230, 230, 230);
          pdf.rect(margin + 5, currentY, barWidth, barHeight, "F");

          pdf.setFillColor(59, 130, 246); // blue
          pdf.rect(margin + 5, currentY, fillWidth, barHeight, "F");

          currentY += 8;
        }
      } else if (block.type === "text_block") {
        const textBlock = block as TextBlock;

        // Filter and process items
        const validItems = textBlock.items.filter((item) => {
          // MESMA LÓGICA DO TextBlock.tsx
          const text = typeof item === "string" ? item : item.text;
          return text && text !== "undefined" && text.trim() !== "";
        });

        validItems.forEach((item, index) => {
          // Handle both string and object formats
          let text = typeof item === "string" ? item : item.text;

          // DEBUG: Log para confirmar que código novo está rodando
          const originalText = text;

          // Limpar texto: Split na seta e pegar só a parte correta
          if (text.includes("→")) {
            logger.log(
              "[PDF] Texto COM seta detectado:",
              text.substring(0, 50)
            );
            // Formato: "❌ texto errado → ✅ texto correto"
            // Queremos: "texto errado → texto correto"
            const parts = text.split("→");
            if (parts.length === 2) {
              // Remove TODOS os não-word chars do início de CADA parte
              // Precisa fazer múltiplas vezes caso tenha múltiplos emojis
              let wrongPart = parts[0].trim();
              let rightPart = parts[1].trim();

              // Remove caracteres especiais do INÍCIO até achar uma letra/número
              wrongPart = wrongPart.replace(/^[^\w]+/g, "").trim();
              rightPart = rightPart.replace(/^[^\w]+/g, "").trim();

              text = `${wrongPart} -> ${rightPart}`;
              logger.log("[PDF] DEPOIS da limpeza:", text.substring(0, 50));
            }
          } else {
            // Sem seta, remove todos emojis do início
            text = text
              .trim()
              .replace(/^[^\w]+/g, "")
              .trim();
          }

          if (originalText !== text) {
            logger.log("[PDF] Texto foi modificado!", {
              antes: originalText.substring(0, 40),
              depois: text.substring(0, 40),
            });
          }

          // Limpeza final - GARANTIR QUE SÓ ASCII VAI PARA O PDF
          text = text
            .replace(/^\d+\.\d+\.\s*/gm, "") // Remove numeração dupla
            .replace(/[''""]/g, "'") // Normaliza quotes curvadas para retas
            .replace(/[–—]/g, "-") // Normaliza dashes
            .replace(/…/g, "...") // Normaliza ellipsis
            // Remove QUALQUER caractere fora do ASCII printável (mantém espaço-~ e →)
            .split("")
            .map((char) => {
              const code = char.charCodeAt(0);
              // Mantém: espaço (32) até ~ (126) OU seta → (8594)
              return (code >= 32 && code <= 126) || code === 8594 ? char : "";
            })
            .join("")
            .replace(/\s+/g, " ") // Remove espaços extras
            .trim();

          logger.log("[PDF] Texto FINAL para PDF:", text);
          logger.log(
            "[PDF] Char codes:",
            [...text]
              .map((c, i) => `${i}:${c}(${c.charCodeAt(0)})`)
              .slice(0, 20)
              .join(" | ")
          );

          checkPageBreak(10);

          pdf.setFontSize(10);
          pdf.setFont("helvetica", "normal");
          pdf.text(`${index + 1}.`, margin + 5, currentY);
          addWrappedText(text, margin + 12, 10, contentWidth - 17, false);
          currentY += 2;
        });
      } else if (block.type === "transcript") {
        const transcript = block as TranscriptBlock;

        // Summary first
        if (transcript.summary) {
          if (transcript.summary.mistakes.sentences.length > 0) {
            checkPageBreak(15);
            pdf.setFontSize(11);
            pdf.setFont("helvetica", "bold");
            pdf.setTextColor(239, 68, 68);
            pdf.text("Areas to Improve", margin + 5, currentY);
            pdf.setTextColor(0, 0, 0);
            currentY += 6;

            transcript.summary.mistakes.sentences.forEach((sentence) => {
              checkPageBreak(8);
              pdf.setFontSize(9);
              pdf.setFont("helvetica", "normal");
              addWrappedText(
                `• ${sentence}`,
                margin + 8,
                9,
                contentWidth - 13,
                false
              );
            });
            currentY += 4;
          }

          if (transcript.summary.corrections.sentences.length > 0) {
            checkPageBreak(15);
            pdf.setFontSize(11);
            pdf.setFont("helvetica", "bold");
            pdf.setTextColor(34, 197, 94);
            pdf.text("Well Done", margin + 5, currentY);
            pdf.setTextColor(0, 0, 0);
            currentY += 6;

            transcript.summary.corrections.sentences.forEach((sentence) => {
              checkPageBreak(8);
              pdf.setFontSize(9);
              pdf.setFont("helvetica", "normal");
              addWrappedText(
                `• ${sentence}`,
                margin + 8,
                9,
                contentWidth - 13,
                false
              );
            });
            currentY += 4;
          }
        }

        // Full conversation
        checkPageBreak(15);
        pdf.setFontSize(11);
        pdf.setFont("helvetica", "bold");
        pdf.text("Full Conversation", margin + 5, currentY);
        currentY += 8;

        for (const message of transcript.transcript) {
          checkPageBreak(12);

          pdf.setFontSize(9);
          pdf.setFont("helvetica", "bold");
          pdf.text(
            message.role === "user" ? "You:" : "Host:",
            margin + 8,
            currentY
          );
          currentY += 5;

          pdf.setFont("helvetica", "normal");
          addWrappedText(
            message.content,
            margin + 8,
            9,
            contentWidth - 13,
            false
          );
          currentY += 3;
        }
      }

      currentY += sectionGap;
    }

    // === FOOTER ===
    const totalPages = (pdf as any).internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text(
        `Page ${i} of ${totalPages} • Generated on ${new Date().toLocaleDateString()}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: "center" }
      );
    }

    // Download
    pdf.save(fileName);
  } catch (error) {
    logger.error("[generateFeedbackPDF] Error generating PDF:", error);
    throw error;
  }
};

/**
 * Share feedback using Web Share API
 */
export const shareFeedback = async (data: {
  title?: string;
  text?: string;
  url?: string;
}): Promise<boolean> => {
  try {
    // Check if Web Share API is supported
    if (!navigator.share) {
      // Fallback: Copy to clipboard
      if (data.url) {
        await navigator.clipboard.writeText(data.url);
        return true;
      }
      throw new Error("Web Share API not supported and no URL provided");
    }

    // Use Web Share API
    await navigator.share({
      title: data.title || "My Conversation Feedback",
      text: data.text || "Check out my conversation performance feedback!",
      url: data.url || window.location.href,
    });

    return true;
  } catch (error) {
    // User cancelled or error occurred
    if ((error as Error).name === "AbortError") {
      // User cancelled, not an error
      return false;
    }
    logger.error("[shareFeedback] Error sharing:", error);
    throw error;
  }
};

/**
 * Copy link to clipboard
 */
export const copyFeedbackLink = async (url?: string): Promise<void> => {
  try {
    const linkToCopy = url || window.location.href;
    await navigator.clipboard.writeText(linkToCopy);
  } catch (error) {
    logger.error("[copyFeedbackLink] Error copying to clipboard:", error);
    throw error;
  }
};

/**
 * Check if Web Share API is supported
 */
export const isShareSupported = (): boolean => {
  return typeof navigator !== "undefined" && !!navigator.share;
};
